import { LocalizedText } from "@/i18n/LocalizedText";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { type HeaderGroup, type Row, flexRender } from "@tanstack/react-table";
import { CardDescription, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "./ui/skeleton";
import { Spinner } from "./ui/spinner";
import noData from "../assets/no-data.svg";
import { Progress } from "./ui/progress";
import { type Table as TableType } from "@tanstack/react-table";
import PaginatedControls from "./Pagination";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { Info } from "lucide-react";

export const ExtendedTable = ({
  table,
  columns,
  pageName = "",
  title = "",
  limitPrompts = null,
  description,
  hiddenPagination = false,
  tooltip,
  pagination = true,
  action = null,
  hiddenTitle = false,
  rowRedirect = null,
  loading = false,
  noDataText = {
    title: "No data",
    description: " We haven't received any data.",
  },
}: {
  rowRedirect?: string | null;
  loading?: boolean;
  hiddenPagination?: boolean;
  pageName?: string;
  limitPrompts?: any;
  action?: null | {
    actionFn: () => void;
    title: string;
    disabled?: boolean;
    tooltipMessage?: string | null;
    onTooltipOpenChange?: (open: boolean) => void;
  };
  pagination?: boolean;
  description?: string;
  tooltip?: { description: string };
  hiddenTitle?: boolean;
  noDataText?: { title: string; description: string };
  table: TableType<any>;
  columns: any;
  title?: string;
}) => {
  const navigate = useNavigate();

  const getWidth = (headerId: string) => {
    if (pageName === "competitor") {
      if (
        ["visibility", "position", "sentiment", "select", "#"].includes(
          headerId,
        )
      ) {
        return "min-w-[40px] max-w-[150px]";
      }
      return "w-full";
    } else if (pageName === "promptsDetails") {
      if (["runDate"].includes(headerId)) {
        return "min-w-[100px] max-w-[150px]";
      }
      if (["position"].includes(headerId)) {
        return "min-w-[80px] max-w-[120px]";
      }
      if (["mentioned", "mentions"].includes(headerId)) {
        return "min-w-[160px] max-w-[180px]";
      }
      return "w-full";
    } else if (pageName === "citedPages") {
      if (["visibility", "sentiment", "select"].includes(headerId)) {
        return "min-w-[40px] max-w-[150px]";
      } else if (["#"].includes(headerId)) {
        return "min-w-[24px] max-w-[48px]";
      }
    } else if (pageName === "citedDomains") {
      if (["visibility", "sentiment", "select"].includes(headerId)) {
        return "min-w-[40px] max-w-[150px]";
      } else if (["#"].includes(headerId)) {
        return "min-w-[24px] w-[48px]";
      } else if (["domain"].includes(headerId)) {
        return "w-full";
      } else if (["type"].includes(headerId)) {
        return "min-w-[120px] w-[150px] max-w-[170px]";
      } else if (["used"].includes(headerId)) {
        return "min-w-[80px] w-[96px] max-w-[110px]";
      }
    } else if (pageName === "strategy") {
      if (["visibility", "sentiment", "select"].includes(headerId)) {
        return "min-w-[40px] max-w-[150px]";
      } else if (["#"].includes(headerId)) {
        return "min-w-[24px] w-[48px]";
      } else if (["domain"].includes(headerId)) {
        return "w-full";
      } else if (["type"].includes(headerId)) {
        return "min-w-[120px] w-[120px] max-w-[170px]";
      } else if (["used"].includes(headerId)) {
        return "min-w-[80px] w-[96px] max-w-[110px]";
      }
    }
    return "";
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between">
        <div>
          {title && (
            <div className="flex items-center gap-2 mb-2">
              <CardTitle>{title}</CardTitle>
              {tooltip && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="cursor-help h-3.5 w-3.5 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>{tooltip.description}</TooltipContent>
                </Tooltip>
              )}
            </div>
          )}

          {description && (
            <CardDescription className="mb-4">
              <span className="@[540px]/card:block text-sm">{description}</span>
            </CardDescription>
          )}
        </div>
      </div>
      <div className="overflow-hidden rounded-md">
        <Table className={`${hiddenTitle && "table-fixed"}`}>
          <TableHeader
            className={`bg-[#E5E5E5] ${hiddenTitle && "opacity-0 collapse pointer-events-none select-none border-none"}`}
          >
            {table.getHeaderGroups().map((headerGroup: HeaderGroup<any>) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      style={hiddenTitle ? { width: header.getSize() } : {}}
                      className={`${getWidth(header.id)}`}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={columns}
                  className="h-24 text-center relative"
                >
                  <div className="relative flex flex-col gap-2">
                    <div className="z-10 gap-1 flex-col absolute flex justify-center items-center w-full h-full">
                      <div className="flex gap-2 items-center flex-col p-2 bg-muted rounded-md">
                        <Spinner
                          className="text-[#FF5E00]"
                          variant="circle"
                        ></Spinner>
                      </div>
                      <div>
                        <LocalizedText id="legacy.loading.results.18j17b" />
                      </div>
                    </div>
                    {Array.from({ length: 6 }).map((_, index) => {
                      return (
                        <div key={index} className="flex w-full relative">
                          <div className="flex items-center gap-2 w-full">
                            <Skeleton className="h-[20px] w-[25px] rounded-full" />
                            <div className="space-y-2 w-full flex gap-2 ">
                              <Skeleton className="h-4 w-[75%]" />
                              <Skeleton className="h-4 w-[25%]" />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row: Row<any>) => (
                <TableRow
                  key={row.id}
                  className={`${rowRedirect && !row.original.mentioned && "cursor-pointer"}`}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <TableCell
                        key={cell.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (
                            rowRedirect &&
                            cell.column.id === "mentioned" &&
                            !cell.getContext().getValue()
                          ) {
                            navigate(rowRedirect);
                          }
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns} className="text-right">
                  <div className="flex flex-col gap-2 items-center my-5">
                    <div>
                      <img src={noData}></img>
                    </div>
                    <div className="text-lg text-foreground font-medium">
                      {noDataText.title}
                    </div>
                    <div className="text-center text-sm text-muted-foreground max-w-[288px] whitespace-break-spaces">
                      {noDataText.description}
                    </div>
                    {action &&
                      (action.disabled && action.tooltipMessage ? (
                        <Tooltip onOpenChange={action.onTooltipOpenChange}>
                          <TooltipTrigger asChild>
                            <span className="inline-flex">
                              <Button
                                className="hover:bg-[#ff8138] hover:text-white text-white cursor-pointer bg-[#FF5E00]"
                                onClick={action.actionFn}
                                disabled
                              >
                                {action.title}
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            className="max-w-[260px] text-center"
                          >
                            {action.tooltipMessage}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Button
                          className="hover:bg-[#ff8138] hover:text-white text-white cursor-pointer bg-[#FF5E00]"
                          onClick={action.actionFn}
                          disabled={Boolean(action.disabled)}
                        >
                          {action.title}
                        </Button>
                      ))}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div
        className={`flex items-center ${limitPrompts ? "justify-between" : "justify-start"} ${hiddenTitle || (hiddenPagination && "hidden")} space-x-2 py-4`}
      >
        {limitPrompts && (
          <div className="flex flex-col items-center gap-4">
            <div className="flex justify-start w-full items-center gap-2">
              <div className="text-muted-foreground text-sm ">
                {limitPrompts.prompts?.used}/{limitPrompts.prompts?.limit}{" "}
                prompts
              </div>
              <Progress
                className="bg-[#ffcdb1] relative h-2 overflow-hidden rounded-full w-[80px]"
                value={
                  //@ts-ignore
                  (parseInt(limitPrompts?.prompts?.used || 0) * 100) /
                  //@ts-ignore
                  parseInt(limitPrompts?.prompts.limit || 1)
                }
              />
            </div>
          </div>
        )}
        {pagination && (
          <div className="flex space-x-1">
            <PaginatedControls table={table} />
          </div>
        )}
      </div>
    </div>
  );
};
